import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { ChevronRight } from 'lucide-react';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import { UploadFile } from './ContextSection/upload_file';
import { TopicQuestionGenerate } from './TopicSection/index';

export const QuestionGenerate: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const tabTitles = [{ label: 'Context Based' }, { label: 'Topic Based' }];

  const renderTabContent = () => {
    if (activeTab === 0) {
      return <UploadFile />;
    } else if (activeTab === 1) {
      return <TopicQuestionGenerate />;
    }
  };

  return (
    <Stack spacing={2}>
      {/* Breadcrumb and Quick Links */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { lg: 'center' },
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          gap: 1.5,
        }}
      >
        <Breadcrumbs
          separator={<ChevronRight size={14} color="#94a3b8" />}
          aria-label="breadcrumb"
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Questions
          </Typography>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Auto Generate Questions
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <PermissionGate
            requiredPermissions={[Permission.QUESTIONS_VIEW]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to="/question-bank"
              variant="caption"
              underline="hover"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.08)' },
              }}
            >
              Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
            requiredPermissions={[Permission.QUESTIONS_VIEW]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to="/pre-screen-bank"
              variant="caption"
              underline="hover"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.08)' },
              }}
            >
              Pre Screen Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
            requiredPermissions={[Permission.JOB_VIEW]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to="/content-manager"
              variant="caption"
              underline="hover"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.08)' },
              }}
            >
              Content Manager
            </Link>
          </PermissionGate>
        </Stack>
      </Box>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ p: 0.5, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val: number) => setActiveTab(val)}
          sx={{
            minHeight: 'auto',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              py: 1,
              px: 2.5,
              minHeight: 'auto',
              borderRadius: 1.5,
            },
          }}
        >
          {tabTitles.map(({ label }, i) => (
            <Tab key={label} label={label} value={i} />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>{renderTabContent()}</Box>
    </Stack>
  );
};
